/**
 * Overflow menu for a single unit row. Lazy-loads the unit's row options when the row
 * renders (i.e. when the property card is expanded), builds the menu entirely from that
 * data, and disables the menu trigger while the request is in flight.
 */
import { useRouter } from 'next/router';

import { AccountViewListingOptionType } from '@zg-rentals/ts-rental-manager-api-client';

import { usePropertyTableOptions } from '../../../hooks/queries/propertyTable';

import { toAppRoute } from '../../../constants/urls';

import { FlexiblePropertyCardOverflowMenu } from './FlexiblePropertyCardOverflowMenu';

import type { ListingAlias, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { OverflowMenuItem } from './FlexiblePropertyCardOverflowMenu';

type Props = {
  listingAlias: ListingAlias;
  unitNumber: string;
  address: RentalManagerAddressDto;
  onArchiveClick: (listingAlias: ListingAlias, address: RentalManagerAddressDto) => void;
};

const UnitOverflowMenu = ({ listingAlias, unitNumber, address, onArchiveClick }: Props) => {
  const router = useRouter();
  const { data, isFetching } = usePropertyTableOptions(listingAlias);

  const items: Array<OverflowMenuItem> = (data?.options ?? []).map((option) => {
    // TODO: BE will need to add an archive option to the unit options endpoint.
    // Archive is handled via a modal rather than navigation.
    if (
      option.type === AccountViewListingOptionType.ARCHIVE ||
      option.type === AccountViewListingOptionType.UNARCHIVE
    ) {
      return { label: option.ctaText, onSelect: () => onArchiveClick(listingAlias, address) };
    }

    return { label: option.ctaText, onSelect: () => router.push(toAppRoute(option.ctaUrl)) };
  });

  return (
    <FlexiblePropertyCardOverflowMenu
      triggerLabel={unitNumber ? `Options for ${unitNumber}` : 'Unit options'}
      items={items}
      disabled={isFetching}
    />
  );
};

export default UnitOverflowMenu;
